// How a project's QA agent should deal with a login screen it encounters during exploration.
// Stored as the `login_mode` project global variable (alongside login_email/login_password) so the
// running container can read it the same way it reads credentials. Lives in netlify/functions/lib so
// it can be imported by BOTH the Netlify functions (e.g. v1.ts) and the client (qaConfig.tsx re-exports
// it) without pulling React/CSS into the server bundle — the same cross-boundary pattern as
// polish-pass-types.ts.
//
//   'provided' — use the login credentials provided at onboarding (login_email/login_password globals)
//   'none'     — do not log in to the app
//   'create'   — register a new account when a login screen is hit
//   'pause'    — pause exploration and ask the user when a login screen is hit
//   'waiting'  — exploration was paused waiting on the user to supply a login (set at runtime)
export const LOGIN_MODES = ['provided', 'none', 'create', 'pause', 'waiting'] as const
export type LoginMode = (typeof LOGIN_MODES)[number]

export function isLoginMode(v: unknown): v is LoginMode {
  return typeof v === 'string' && (LOGIN_MODES as readonly string[]).includes(v)
}

// How the user actually ANSWERED the login question during onboarding, when they answered at all.
// Needed because the answer is otherwise invisible in the draft config: "let QA register its own
// account" and "never touched the login step" both look like logins=[] + autoCreateAccount=true (the
// default), so without this the chat surface had to assume the latter and ask again at the wall.
export const LOGIN_CHOICES = ['provided', 'self-signup', 'skip'] as const
export type LoginChoice = (typeof LOGIN_CHOICES)[number]

/** Pick the login_mode to persist at onboarding time. Explicit signals win: real provided credentials
 *  mean 'provided', an opt-out of self-registration with no credentials means 'none', and an explicit
 *  "let QA register its own account" means 'create' — that IS the answer to the login question, so
 *  pausing to ask it again at the wall would ignore the user. Otherwise fall back to the per-surface
 *  default for an UNANSWERED login question — 'pause' for the chat UI (which can ask in-place), 'create'
 *  everywhere else. */
export function resolveLoginMode(opts: {
  logins?: { email: string; password: string }[]
  autoCreateAccount: boolean
  isChatOnboarding: boolean
  loginChoice?: LoginChoice
}): LoginMode {
  const hasProvidedLogins = (opts.logins ?? []).some((l) => l.email.trim() && l.password.trim())
  if (hasProvidedLogins) return 'provided'
  if (!opts.autoCreateAccount) return 'none'
  if (opts.loginChoice === 'self-signup') return 'create'
  return opts.isChatOnboarding ? 'pause' : 'create'
}

/** The project global variable that carries the login mode. */
export function loginModeGlobalVariable(mode: LoginMode): { name: string; init: string } {
  return { name: 'login_mode', init: mode }
}
